import type { StorageManager } from '@/storage/save-data';
import type { LevelData } from '@/game/level';

const AUTOSAVE_DEBOUNCE_MS = 2000;

export class EditorStorage {
  private current: LevelData | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private sm: StorageManager) {}

  setCurrentLevel(level: LevelData): void {
    this.current = level;
  }

  /** Call after every command; schedules a debounced autosave write. */
  noteChange(): void {
    if (!this.current) return;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.writeAutosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  /** Write any pending autosave right now (e.g., on beforeunload). */
  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
      this.writeAutosave();
    }
  }

  private writeAutosave(): void {
    if (!this.current) return;
    if (this.current.meta.remoteId) {
      this.current.meta.remoteDirty = true;
    }
    this.sm.setAutosave(this.current.meta.id, this.current);
    this.timer = null;
  }

  /** Commit the current level to userLevels and clear autosave. */
  save(): void {
    if (!this.current) return;
    const now = new Date().toISOString();
    this.current.meta.updatedAt = now;
    if (!this.current.meta.createdAt) this.current.meta.createdAt = now;
    if (this.current.meta.remoteId) {
      this.current.meta.remoteDirty = true;
    }
    this.sm.upsertUserLevel(this.current);
    this.sm.clearAutosave();
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
  }

  /** Autosave exists AND is newer than the stored copy's updatedAt. */
  recoveryAvailable(levelId: string): boolean {
    const autosave = this.sm.getAutosave();
    if (!autosave || autosave.levelId !== levelId) return false;
    const stored = this.sm.getUserLevel(levelId);
    if (!stored) return true; // autosave without a stored level is always recoverable
    const storedTs = stored.meta.updatedAt ? Date.parse(stored.meta.updatedAt) : 0;
    return autosave.ts > storedTs;
  }

  recoverAutosave(): LevelData | null {
    const autosave = this.sm.getAutosave();
    return autosave?.data ?? null;
  }

  discardAutosave(): void { this.sm.clearAutosave(); }
}
